import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import {
  Users,
  Shield,
  Plus,
  Edit2,
  Activity,
  Clock,
  Key
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminSubscriptions = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);

  useEffect(() => {
    fetchSubscriptionTiers();
  }, []);

  const fetchSubscriptionTiers = async () => {
    try {
      const response = await adminAPI.getSubscriptionTiers();
      setTiers(response.data.data.tiers);
    } catch (error) {
      toast.error('Failed to fetch subscription tiers');
      console.error('Error fetching subscription tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = async (tierData) => {
    try {
      const response = await adminAPI.createSubscriptionTier(tierData);
      setTiers([...tiers, response.data.data.tier]);
      setShowCreateModal(false);
      toast.success('Subscription tier created successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create subscription tier');
    }
  };

  const handleUpdateTier = async (tierId, tierData) => {
    try {
      const response = await adminAPI.updateSubscriptionTier(tierId, tierData);
      setTiers(tiers.map(tier => tier.id === tierId ? response.data.data.tier : tier));
      setShowEditModal(false);
      setEditingTier(null);
      toast.success('Subscription tier updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update subscription tier');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Shield className="w-8 h-8 mr-3 text-blue-600" />
              Subscription Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage subscription tiers and user limits
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-md btn-primary flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Tier
          </button>
        </div>
      </div>

      {/* Subscription Tiers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className={`card p-6 border-2 ${
              tier.name === 'free'
                ? 'border-gray-300 bg-gray-50'
                : tier.name === 'donator'
                ? 'border-blue-300 bg-blue-50'
                : 'border-purple-300 bg-purple-50'
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 capitalize">
                {tier.displayName || tier.name}
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    setEditingTier(tier);
                    setShowEditModal(true);
                  }}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-100 rounded"
                  title="Edit tier"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Key className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Max API Keys</span>
                </div>
                <span className="font-medium">{tier.maxApiKeys}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Activity className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Daily Limit</span>
                </div>
                <span className="font-medium">{tier.dailyRequestLimit.toLocaleString()}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Minute Limit</span>
                </div>
                <span className="font-medium">{tier.minuteRequestLimit.toLocaleString()}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="w-4 h-4 text-gray-500 mr-2" />
                  <span className="text-sm text-gray-600">Users</span>
                </div>
                <span className="font-medium">{tier.userCount || 0}</span>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Status</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  tier.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {tier.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Tier Modal */}
      <TierModal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setEditingTier(null);
        }}
        onSave={editingTier ?
          (data) => handleUpdateTier(editingTier.id, data) :
          handleCreateTier
        }
        tier={editingTier}
        mode={editingTier ? 'edit' : 'create'}
      />
    </div>
  );
};

// Tier Creation/Editing Modal Component
const TierModal = ({ isOpen, onClose, onSave, tier, mode }) => {
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    maxApiKeys: 5,
    dailyRequestLimit: 10000,
    minuteRequestLimit: 100
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (tier && mode === 'edit') {
      setFormData({
        name: tier.name || '',
        displayName: tier.displayName || '',
        maxApiKeys: tier.maxApiKeys || 5,
        dailyRequestLimit: tier.dailyRequestLimit || 10000,
        minuteRequestLimit: tier.minuteRequestLimit || 100
      });
    } else {
      setFormData({
        name: '',
        displayName: '',
        maxApiKeys: 5,
        dailyRequestLimit: 10000,
        minuteRequestLimit: 100
      });
    }
  }, [tier, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving tier:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'edit' ? 'Edit Subscription Tier' : 'Create Subscription Tier'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {mode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., premium, enterprise"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase, no spaces. Used internally for API references.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name *
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Premium Plan, Enterprise"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max API Keys *
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.maxApiKeys}
                onChange={(e) => setFormData({ ...formData, maxApiKeys: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Daily Request Limit *
              </label>
              <input
                type="number"
                min="1"
                max="10000000"
                value={formData.dailyRequestLimit}
                onChange={(e) => setFormData({ ...formData, dailyRequestLimit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minute Request Limit *
              </label>
              <input
                type="number"
                min="1"
                max="100000"
                value={formData.minuteRequestLimit}
                onChange={(e) => setFormData({ ...formData, minuteRequestLimit: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-md btn-soft-gray"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-md btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'edit' ? 'Update Tier' : 'Create Tier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSubscriptions;